import React, { FC, useCallback, useState } from "react";
import { format, isValid, parseISO } from "date-fns";

import { Button, ButtonProps, Dialog, Input, LoadingSpinner, Select, SelectItem } from "@xapp/components";

import {
    ServiceTitanBookingProvider,
    useServiceTitanBookingProvidersQuery,
    useServiceTitanDefaultsQuery,
    useValidateServiceTitanBookingProviderQuery,
} from "generated/graphql";

import { IntegrationConnectionProps } from "./BasicIntegrationCard";

/**
 * ServiceTitan's production endpoints. The API strips protocols before storing these, so they
 * are kept in the same protocol-less form the backend defaults to.
 */
export const SERVICE_TITAN_AUTH_ENDPOINT = "auth.servicetitan.io/connect/token";
export const SERVICE_TITAN_API_ENDPOINT = "api.servicetitan.io";

export const SERVICE_TITAN_BOOKING_PROVIDER_DOCS_URL =
    "https://documentation.xapp.ai/help/integrations/integration-service-titan/#step-3";

export interface ServiceTitanConnectionProps extends IntegrationConnectionProps {
    integration: "servicetitan";
    tenantId: string;
    clientId: string;
    clientSecret: string;
    serviceTitanAppId: string;
    authEndpoint: string;
    apiEndpoint: string;
    bookingProviderId: string;
    defaultJobTypeId: number;
    defaultCampaignId: number;
    defaultBusinessUnitId: number;
}

export interface ServiceTitanConnectDialogProps {
    appId: string;
    /**
     * The dialog is open.
     */
    active: boolean;
    /**
     * Dialog dismissed, either by cancelling or after a successful connect.
     */
    onClose(): void;
    /**
     * User confirmed the full configuration; submit it to addServiceTitanIntegration.
     */
    onConnect?(props: ServiceTitanConnectionProps): void;
}

type ConnectStep = "credentials" | "provider" | "defaults" | "confirm";

const STEP_TITLES: Record<ConnectStep, string> = {
    credentials: "Step 1 of 4 — Credentials",
    provider: "Step 2 of 4 — Booking Provider",
    defaults: "Step 3 of 4 — Defaults",
    confirm: "Step 4 of 4 — Confirm",
};

const helperStyle: React.CSSProperties = { color: "#666", fontSize: "12px", marginTop: "4px" };
const errorStyle: React.CSSProperties = { color: "red", fontSize: "12px", marginTop: "4px" };

/**
 * Label a discovered booking provider by its source with enough booking history for the
 * customer to recognise which one is theirs.
 */
export function bookingProviderLabel(provider: ServiceTitanBookingProvider): string {
    const bookings = provider.bookingCount === 1 ? "1 booking" : `${provider.bookingCount} bookings`;
    let label = `${provider.source} — ${bookings}`;
    if (provider.lastBookingCreatedOn) {
        const lastBooking = parseISO(provider.lastBookingCreatedOn);
        if (isValid(lastBooking)) {
            label += `, last ${format(lastBooking, "MMM d, yyyy")}`;
        }
    }
    return label;
}

/**
 * The self-serve ServiceTitan connect flow: credentials, then a booking-provider picker over
 * what discovery finds in the tenant, then default job type / campaign / business unit
 * pickers, then confirm. Discovery runs against the credentials entered in step one, before
 * anything is saved.
 */
export const ServiceTitanConnectDialog: FC<ServiceTitanConnectDialogProps> = (props) => {
    const { appId, active, onClose, onConnect } = props;

    const [step, setStep] = useState<ConnectStep>("credentials");
    const [tenantId, setTenantId] = useState<string>("");
    const [clientId, setClientId] = useState<string>("");
    const [clientSecret, setClientSecret] = useState<string>("");
    const [serviceTitanAppId, setServiceTitanAppId] = useState<string>("");
    const [selectedProviderId, setSelectedProviderId] = useState<string>("");
    const [manualProviderId, setManualProviderId] = useState<string>("");
    // The manually entered ID currently being validated; empty pauses the validation query.
    const [validateProviderId, setValidateProviderId] = useState<string>("");
    const [defaultJobTypeId, setDefaultJobTypeId] = useState<string>("");
    const [defaultCampaignId, setDefaultCampaignId] = useState<string>("");
    const [defaultBusinessUnitId, setDefaultBusinessUnitId] = useState<string>("");

    // Providers and defaults belong to the tenant they were discovered in, so any credential edit
    // drops them rather than letting a pick from the previous tenant be submitted with the new one.
    const handleCredentialChange = useCallback(
        (setCredential: (value: string) => void) => (value: string | number) => {
            setCredential(String(value));
            setSelectedProviderId("");
            setManualProviderId("");
            setValidateProviderId("");
            setDefaultJobTypeId("");
            setDefaultCampaignId("");
            setDefaultBusinessUnitId("");
        },
        []
    );

    // The dialog stays mounted while closed, so closing starts the next open over from empty
    // credentials instead of resuming a half-finished setup.
    const handleClose = useCallback(() => {
        setStep("credentials");
        setTenantId("");
        setClientId("");
        setClientSecret("");
        setServiceTitanAppId("");
        setSelectedProviderId("");
        setManualProviderId("");
        setValidateProviderId("");
        setDefaultJobTypeId("");
        setDefaultCampaignId("");
        setDefaultBusinessUnitId("");
        onClose();
    }, [onClose]);

    const hasCredentials: boolean =
        !!tenantId.trim() && !!clientId.trim() && !!clientSecret.trim() && !!serviceTitanAppId.trim();

    const credentialVariables = {
        appId,
        tenantId: tenantId.trim(),
        clientId: clientId.trim(),
        clientSecret: clientSecret.trim(),
        serviceTitanAppId: serviceTitanAppId.trim(),
        authEndpoint: SERVICE_TITAN_AUTH_ENDPOINT,
        apiEndpoint: SERVICE_TITAN_API_ENDPOINT,
    };

    const [providersResult, refetchProviders] = useServiceTitanBookingProvidersQuery({
        pause: step === "credentials" || !hasCredentials,
        variables: credentialVariables,
    });

    const [defaultsResult, refetchDefaults] = useServiceTitanDefaultsQuery({
        pause: (step !== "defaults" && step !== "confirm") || !hasCredentials,
        variables: credentialVariables,
    });

    const [validationResult] = useValidateServiceTitanBookingProviderQuery({
        pause: !validateProviderId,
        variables: { ...credentialVariables, bookingProviderId: validateProviderId },
    });

    const providers = providersResult.data?.app?.serviceTitanBookingProviders;
    const manualId = manualProviderId.trim();
    // Only trust a validation result that is for the ID currently in the input.
    const validation =
        validateProviderId && validateProviderId === manualId && !validationResult.fetching
            ? validationResult.data?.app?.validateServiceTitanBookingProvider
            : undefined;

    const chosenProviderId = manualId || selectedProviderId;
    const chosenProvider = providers?.find((p) => p.bookingProviderId === chosenProviderId);

    const jobTypes = defaultsResult.data?.app?.serviceTitanJobTypes;
    const campaigns = defaultsResult.data?.app?.serviceTitanCampaigns;
    const businessUnits = defaultsResult.data?.app?.serviceTitanBusinessUnits;

    // Real opportunity types first: a non-opportunity default stamps every unclassified
    // lead as dead on arrival, so the picker steers away from them.
    const sortedJobTypes = jobTypes
        ? [...jobTypes].sort((a, b) => Number(a.nonOpportunity) - Number(b.nonOpportunity))
        : undefined;
    const jobTypeItems: SelectItem[] | undefined = sortedJobTypes?.map((jobType) => ({
        label: jobType.nonOpportunity ? `${jobType.name} — non-opportunity` : jobType.name,
        value: jobType.id,
    }));
    const selectedJobType = jobTypes?.find((jobType) => jobType.id === defaultJobTypeId);

    const handleBack = useCallback(() => {
        setStep((current) => {
            if (current === "confirm") {
                return "defaults";
            }
            if (current === "defaults") {
                return "provider";
            }
            return "credentials";
        });
    }, []);

    const handleNext = useCallback(() => {
        if (step === "credentials") {
            if (hasCredentials) {
                setStep("provider");
            }
            return;
        }
        if (step === "provider") {
            if (manualId) {
                if (validation?.valid) {
                    setStep("defaults");
                } else if (!validation) {
                    // Request (or re-request after an edit) the validation check. A failed
                    // check keeps the flow here until the ID changes.
                    setValidateProviderId(manualId);
                }
                return;
            }
            if (selectedProviderId) {
                setStep("defaults");
            }
            return;
        }
        if (step === "defaults") {
            if (defaultJobTypeId && defaultCampaignId && defaultBusinessUnitId) {
                setStep("confirm");
            }
        }
    }, [
        step,
        hasCredentials,
        manualId,
        validation,
        selectedProviderId,
        defaultJobTypeId,
        defaultCampaignId,
        defaultBusinessUnitId,
    ]);

    const handleConnect = useCallback(() => {
        if (typeof onConnect === "function") {
            onConnect({
                integration: "servicetitan",
                tenantId: tenantId.trim(),
                clientId: clientId.trim(),
                clientSecret: clientSecret.trim(),
                serviceTitanAppId: serviceTitanAppId.trim(),
                authEndpoint: SERVICE_TITAN_AUTH_ENDPOINT,
                apiEndpoint: SERVICE_TITAN_API_ENDPOINT,
                bookingProviderId: chosenProviderId,
                defaultJobTypeId: Number(defaultJobTypeId),
                defaultCampaignId: Number(defaultCampaignId),
                defaultBusinessUnitId: Number(defaultBusinessUnitId),
            });
        }
        handleClose();
    }, [
        onConnect,
        handleClose,
        tenantId,
        clientId,
        clientSecret,
        serviceTitanAppId,
        chosenProviderId,
        defaultJobTypeId,
        defaultCampaignId,
        defaultBusinessUnitId,
    ]);

    let nextDisabled: boolean;
    if (step === "credentials") {
        nextDisabled = !hasCredentials;
    } else if (step === "provider") {
        nextDisabled = manualId ? validationResult.fetching : !selectedProviderId;
    } else {
        nextDisabled = !defaultJobTypeId || !defaultCampaignId || !defaultBusinessUnitId;
    }

    const actions: ButtonProps[] = [
        {
            label: "Cancel",
            variation: "secondary",
            onClick: handleClose,
        },
    ];

    if (step !== "credentials") {
        actions.push({
            label: "Back",
            variation: "secondary",
            onClick: handleBack,
        });
    }

    if (step === "confirm") {
        actions.push({
            label: "Connect",
            onClick: handleConnect,
        });
    } else {
        actions.push({
            label: "Next",
            disabled: nextDisabled,
            onClick: handleNext,
        });
    }

    return (
        <Dialog active={active} actions={actions} title="ServiceTitan Setup" onClose={handleClose}>
            <div style={{ display: "flex", flexDirection: "column", rowGap: "10px" }}>
                <b>{STEP_TITLES[step]}</b>
                {step === "credentials" && (
                    <>
                        <div>
                            <p>
                                Enter the API application credentials from your ServiceTitan account. You can create
                                them under "Integrations" then "API Application Access".
                            </p>
                            <p>
                                Complete instructions can be found{" "}
                                <a
                                    target="_blank"
                                    href="https://documentation.xapp.ai/help/integrations/integration-service-titan"
                                    rel="noreferrer"
                                >
                                    here
                                </a>
                                .
                            </p>
                        </div>
                        <div>
                            <Input
                                label="Tenant ID"
                                value={tenantId}
                                onChange={handleCredentialChange(setTenantId)}
                            />
                        </div>
                        <div>
                            <Input
                                label="Client ID"
                                value={clientId}
                                onChange={handleCredentialChange(setClientId)}
                            />
                        </div>
                        <div>
                            <Input
                                label="Client Secret"
                                type="password"
                                value={clientSecret}
                                onChange={handleCredentialChange(setClientSecret)}
                            />
                        </div>
                        <div>
                            <Input
                                label="App Key"
                                value={serviceTitanAppId}
                                onChange={handleCredentialChange(setServiceTitanAppId)}
                            />
                        </div>
                    </>
                )}
                {step === "provider" && (
                    <>
                        <p>
                            Pick the booking provider we should create bookings under. These were discovered from the
                            bookings already in your ServiceTitan account.
                        </p>
                        {providersResult.fetching && <LoadingSpinner loadingText="Discovering booking providers..." />}
                        {!providersResult.fetching && providersResult.error && (
                            <div>
                                <p data-testid="st-providers-error" style={errorStyle}>
                                    We could not load the booking providers from your ServiceTitan account. Check the
                                    credentials from the previous step and try again.
                                </p>
                                <Button
                                    data-testid="st-retry-providers"
                                    label="Retry"
                                    variation="secondary"
                                    onClick={() => refetchProviders({ requestPolicy: "network-only" })}
                                />
                            </div>
                        )}
                        {!providersResult.fetching && !providersResult.error && providers && providers.length > 0 && (
                            <div>
                                <Select
                                    label="Booking Provider"
                                    fullWidth={true}
                                    items={providers.map((provider) => ({
                                        label: bookingProviderLabel(provider),
                                        value: provider.bookingProviderId,
                                    }))}
                                    value={selectedProviderId}
                                    onChange={(value) => {
                                        setSelectedProviderId(String(value));
                                        setManualProviderId("");
                                        setValidateProviderId("");
                                    }}
                                />
                            </div>
                        )}
                        {!providersResult.fetching && !providersResult.error && providers && providers.length === 0 && (
                            <div>
                                <p>
                                    No booking providers were found in your ServiceTitan account yet. That is expected
                                    for a brand new tenant with no bookings — a booking provider only becomes visible
                                    once it has taken a booking.
                                </p>
                                <p>
                                    Follow{" "}
                                    <a target="_blank" href={SERVICE_TITAN_BOOKING_PROVIDER_DOCS_URL} rel="noreferrer">
                                        the setup guide
                                    </a>{" "}
                                    to create one, then enter its ID below.
                                </p>
                            </div>
                        )}
                        <div>
                            <Input
                                label="Booking Provider ID"
                                value={manualProviderId}
                                onChange={(value) => {
                                    setManualProviderId(String(value));
                                    setValidateProviderId("");
                                }}
                            />
                            <p style={helperStyle}>
                                Not seeing yours? Enter the booking provider ID manually and we will check it against
                                your account before continuing.
                            </p>
                            {manualId && validateProviderId === manualId && validationResult.fetching && (
                                <p style={helperStyle}>Checking the booking provider...</p>
                            )}
                            {validation && !validation.valid && (
                                <p data-testid="st-validation-error" style={errorStyle}>
                                    {validation.message ||
                                        "That booking provider ID did not check out against your ServiceTitan account."}
                                </p>
                            )}
                            {validation && validation.valid && (
                                <p style={{ color: "green", fontSize: "12px", marginTop: "4px" }}>
                                    Booking provider looks good. Click Next to continue.
                                </p>
                            )}
                        </div>
                    </>
                )}
                {step === "defaults" && (
                    <>
                        <p>
                            Pick the defaults we should use when creating jobs from your leads. The default job type is
                            used whenever we cannot classify a lead into a more specific type.
                        </p>
                        {defaultsResult.fetching && <LoadingSpinner loadingText="Loading your ServiceTitan setup..." />}
                        {!defaultsResult.fetching && defaultsResult.error && (
                            <div>
                                <p data-testid="st-defaults-error" style={errorStyle}>
                                    We could not load the job types, campaigns and business units from your ServiceTitan
                                    account.
                                </p>
                                <Button
                                    data-testid="st-retry-defaults"
                                    label="Retry"
                                    variation="secondary"
                                    onClick={() => refetchDefaults({ requestPolicy: "network-only" })}
                                />
                            </div>
                        )}
                        {!defaultsResult.fetching && !defaultsResult.error && jobTypeItems && (
                            <div>
                                <Select
                                    label="Default Job Type"
                                    fullWidth={true}
                                    items={jobTypeItems}
                                    value={defaultJobTypeId}
                                    onChange={(value) => setDefaultJobTypeId(String(value))}
                                />
                                {selectedJobType?.nonOpportunity && (
                                    <p data-testid="st-non-opportunity-warning" style={errorStyle}>
                                        "{selectedJobType.name}" reads as a non-opportunity type. Every lead we cannot
                                        classify is stamped with this default, so those leads would look dead on arrival
                                        to your CSRs. Consider the type your own team uses for real work instead.
                                    </p>
                                )}
                            </div>
                        )}
                        {!defaultsResult.fetching && !defaultsResult.error && campaigns && (
                            <div>
                                <Select
                                    label="Default Campaign"
                                    fullWidth={true}
                                    items={campaigns.map((campaign) => ({ label: campaign.name, value: campaign.id }))}
                                    value={defaultCampaignId}
                                    onChange={(value) => setDefaultCampaignId(String(value))}
                                />
                            </div>
                        )}
                        {!defaultsResult.fetching && !defaultsResult.error && businessUnits && (
                            <div>
                                <Select
                                    label="Default Business Unit"
                                    fullWidth={true}
                                    items={businessUnits.map((unit) => ({ label: unit.name, value: unit.id }))}
                                    value={defaultBusinessUnitId}
                                    onChange={(value) => setDefaultBusinessUnitId(String(value))}
                                />
                            </div>
                        )}
                    </>
                )}
                {step === "confirm" && (
                    <div>
                        <p>Ready to connect ServiceTitan with the following configuration:</p>
                        <ul>
                            <li>
                                <b>Tenant ID:</b> {tenantId.trim()}
                            </li>
                            <li>
                                <b>Booking Provider:</b>{" "}
                                {chosenProvider ? bookingProviderLabel(chosenProvider) : chosenProviderId}
                            </li>
                            <li>
                                <b>Default Job Type:</b> {selectedJobType?.name || defaultJobTypeId}
                            </li>
                            <li>
                                <b>Default Campaign:</b>{" "}
                                {campaigns?.find((campaign) => campaign.id === defaultCampaignId)?.name ||
                                    defaultCampaignId}
                            </li>
                            <li>
                                <b>Default Business Unit:</b>{" "}
                                {businessUnits?.find((unit) => unit.id === defaultBusinessUnitId)?.name ||
                                    defaultBusinessUnitId}
                            </li>
                        </ul>
                    </div>
                )}
            </div>
        </Dialog>
    );
};
